import { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, IconButton,
    Modal, ModalDialog, ModalClose, Input, Select, Option, Alert,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon  from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon        from '@mui/icons-material/AddRounded';
import EditRoundedIcon       from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon     from '@mui/icons-material/DeleteRounded';
import PhoneIphoneRoundedIcon from '@mui/icons-material/PhoneIphoneRounded';

import Header from '../../components/Header';
import { AppContext } from '../../context';
import {
    APIGetPaymentNumbers, APICreatePaymentNumber,
    APIUpdatePaymentNumber, APIDeletePaymentNumber,
    APIGetLocations, parseApiError,
    APIGetBankBrands, APIUpdateBankBrand, APIUploadBankLogo, APIDeleteBankLogo,
    API_URL,
} from '../../api';
import MemeLoader from '../../components/MemeLoader';
import { BANKS, BankBadge, formatPhone } from '../../components/BankBrand';



function PaymentNumberModal({ open, item, locations, onClose, onSave, loading, error }) {
    const isEdit = !!item;
    const [form, setForm] = useState({
        phone: '', bank: 'alpha', owner_name: '',
        location_id: null, comment: '', is_active: true,
    });

    useEffect(() => {
        if (open) {
            if (item) {
                setForm({
                    phone:        item.phone || '',
                    bank:         item.bank || 'alpha',
                    owner_name:   item.owner_name || '',
                    location_id:  item.location || null,
                    comment:      item.comment || '',
                    is_active:    item.is_active ?? true,
                });
            } else {
                setForm({ phone: '', bank: 'alpha', owner_name: '', location_id: null, comment: '', is_active: true });
            }
        }
    }, [open, item]);

    const set = field => e => {
        const val = typeof e === 'string' ? e : e.target.value;
        setForm(p => ({ ...p, [field]: val }));
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.55)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 440, width: '100%', p: 3,
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                    {isEdit ? 'Редактировать номер' : 'Новый номер'}
                </Typography>

                <Stack spacing={1.8}>
                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Телефон *
                        </Typography>
                        <Input
                            value={form.phone}
                            onChange={set('phone')}
                            placeholder="+7 999 077 77 77"
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Банк *
                        </Typography>
                        <Select
                            value={form.bank}
                            onChange={(_, val) => val && setForm(p => ({ ...p, bank: val }))}
                        >
                            {Object.entries(BANKS).map(([code, meta]) => (
                                <Option key={code} value={code}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <BankBadge bank={code} size={18} />
                                        {meta.name}
                                    </Box>
                                </Option>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Имя владельца *
                        </Typography>
                        <Input
                            value={form.owner_name}
                            onChange={set('owner_name')}
                            placeholder="Иван И."
                        />
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Точка (опционально)
                        </Typography>
                        <Select
                            value={form.location_id ?? ''}
                            onChange={(_, val) => setForm(p => ({ ...p, location_id: val === '' ? null : val }))}
                            placeholder="Доступен на всех точках"
                        >
                            <Option value="">Доступен на всех точках</Option>
                            {locations.map(l => (
                                <Option key={l.id} value={l.id}>{l.name}</Option>
                            ))}
                        </Select>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'var(--joy-palette-text-secondary)', mb: 0.6 }}>
                            Комментарий
                        </Typography>
                        <Input
                            value={form.comment}
                            onChange={set('comment')}
                            placeholder="..."
                        />
                    </Box>

                    {error && (
                        <Alert color="danger" variant="soft" size="sm">
                            {error}
                        </Alert>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, pt: 0.5 }}>
                        <Button
                            onClick={() => onSave(form)}
                            loading={loading}
                            sx={{
                                flex: 1,
                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                color: '#fff', fontWeight: 700,
                                boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                            }}
                        >
                            {isEdit ? 'Сохранить' : 'Создать'}
                        </Button>
                        <Button
                            variant="outlined" onClick={onClose}
                            sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)' }}
                        >
                            Отмена
                        </Button>
                    </Box>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

function BankBrandingModal({ open, onClose, token, showToast }) {
    // brandMap: { [bankCode]: { logo_url, color } } — данные с сервера
    const [brandMap, setBrandMap] = useState({});
    const [loading, setLoading] = useState(false);
    const [busyBank, setBusyBank] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const data = await APIGetBankBrands(token);
        const map = {};
        (Array.isArray(data) ? data : []).forEach(b => {
            map[b.bank] = { logo_url: b.logo_url || null, color: b.color || '' };
        });
        setBrandMap(map);
        setLoading(false);
    }, [token]);

    useEffect(() => { if (open) load(); }, [open, load]);

    const handleColor = async (bank, color) => {
        setBrandMap(prev => ({ ...prev, [bank]: { ...prev[bank], color } }));
        const res = await APIUpdateBankBrand(token, bank, color);
        if (res?.error) showToast(parseApiError(res.error, 'Не удалось сохранить цвет'), 'danger');
    };

    const handleUpload = async (bank, file) => {
        if (!file) return;
        setBusyBank(bank);
        const res = await APIUploadBankLogo(token, bank, file);
        setBusyBank(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось загрузить логотип'), 'danger'); return; }
        showToast('Логотип загружен ✓');
        setBrandMap(prev => ({ ...prev, [bank]: { ...prev[bank], logo_url: res.logo_url } }));
    };

    const handleDelete = async (bank) => {
        setBusyBank(bank);
        const res = await APIDeleteBankLogo(token, bank);
        setBusyBank(null);
        if (res?.error) { showToast(parseApiError(res.error, 'Не удалось удалить логотип'), 'danger'); return; }
        setBrandMap(prev => ({ ...prev, [bank]: { ...prev[bank], logo_url: null } }));
    };

    // Список банков — из встроенного каталога BANKS (кроме служебного 'other')
    const bankList = Object.entries(BANKS).filter(([code]) => code !== 'other');

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.55)' } } }}>
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 480, width: '100%', p: 3, maxHeight: '92vh', overflowY: 'auto',
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.5 }}>Брендинг банков</Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)', mb: 2 }}>
                    Логотип и цвет применяются в окне оплаты у фотографа и в списках. Пусто — берётся
                    встроенный вид банка.
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><MemeLoader /></Box>
                ) : (
                    <Stack spacing={1.2}>
                        {bankList.map(([code, meta]) => {
                            const b = brandMap[code] || {};
                            return (
                                <Box key={code} sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.2,
                                    p: 1, borderRadius: 'var(--joy-radius-md)',
                                    bgcolor: 'var(--joy-palette-background-level2)',
                                }}>
                                    <BankBadge bank={code} size={36} logoUrl={b.logo_url} color={b.color} />
                                    <Typography sx={{ flex: 1, fontSize: 13, fontWeight: 700 }}>
                                        {meta.name}
                                    </Typography>
                                    {/* Цвет */}
                                    <input
                                        type="color"
                                        value={b.color || meta.color}
                                        onChange={(e) => handleColor(code, e.target.value)}
                                        title="Цвет банка"
                                        style={{ width: 34, height: 34, border: 'none', background: 'none', cursor: 'pointer' }}
                                    />
                                    {/* Загрузка логотипа */}
                                    <Button component="label" size="sm" variant="soft"
                                        loading={busyBank === code} sx={{ fontWeight: 700 }}>
                                        Логотип
                                        <input hidden type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml"
                                            onChange={(e) => { handleUpload(code, e.target.files?.[0]); e.target.value = ''; }} />
                                    </Button>
                                    {b.logo_url && (
                                        <IconButton size="sm" onClick={() => handleDelete(code)} title="Удалить логотип">
                                            <DeleteRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                                        </IconButton>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    );
}


export default function PaymentNumbersPage() {
    const { token, showToast, hasPerm, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();
    const canManage = hasPerm('payment_numbers.manage');

    const [numbers,   setNumbers]   = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading,   setLoading]   = useState(true);
    const [editModal, setEditModal] = useState({ open: false, item: null, loading: false, error: '' });
    const [brandingOpen, setBrandingOpen] = useState(false);
    const [deleting,  setDeleting]  = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const [nums, locs] = await Promise.all([
            APIGetPaymentNumbers(token),
            APIGetLocations(token),
        ]);
        if (Array.isArray(nums)) setNumbers(nums);
        if (Array.isArray(locs)) setLocations(locs);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    const handleSave = async (form) => {
        setEditModal(p => ({ ...p, loading: true, error: '' }));
        const payload = { ...form };
        const isEdit = !!editModal.item;
        const res = isEdit
            ? await APIUpdatePaymentNumber(token, editModal.item.id, payload)
            : await APICreatePaymentNumber(token, payload);
        if (res?.error) {
            setEditModal(p => ({ ...p, loading: false, error: parseApiError(res.error) }));
            return;
        }
        showToast(isEdit ? 'Номер обновлён ✓' : 'Номер добавлен ✓');
        setEditModal({ open: false, item: null, loading: false, error: '' });
        loadAll();
    };

    const handleDelete = async (item) => {
        if (!window.confirm(`Удалить номер ${formatPhone(item.phone)}?`)) return;
        setDeleting(item.id);
        const res = await APIDeletePaymentNumber(token, item.id);
        setDeleting(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Не удалось удалить'), 'danger');
            return;
        }
        showToast('Номер удалён', 'warning');
        setNumbers(prev => prev.filter(n => n.id !== item.id));
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            style={{ minHeight: '100vh', background: 'var(--joy-palette-background-body)' }}
        >
            <Header />
            <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 2, sm: 3 }, py: 3 }}>

                {/* ── Шапка ─────────────────────────────────── */}
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <IconButton variant="plain" onClick={() => navigate('/manager')} sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                            <ArrowBackRoundedIcon />
                        </IconButton>
                        <Box>
                            <Typography sx={{ fontSize: 22, fontWeight: 800, color: 'var(--joy-palette-text-primary)' }}>
                                Номера для оплаты
                            </Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-secondary)', mt: 0.3 }}>
                                Пул номеров, которые фотографы получают для приёма переводов
                            </Typography>
                        </Box>
                    </Box>
                    {canManage && (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="outlined"
                                onClick={() => setBrandingOpen(true)}
                                sx={{ borderColor: 'var(--joy-palette-divider)', color: 'var(--joy-palette-text-secondary)', fontWeight: 700 }}
                            >
                                Брендинг банков
                            </Button>
                            <Button
                                startDecorator={<AddRoundedIcon />}
                                onClick={() => setEditModal({ open: true, item: null, loading: false, error: '' })}
                                sx={{
                                    background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                    color: '#fff', fontWeight: 700,
                                    boxShadow: '0 4px 16px rgba(162,38,226,0.35)',
                                }}
                            >
                                Добавить номер
                            </Button>
                        </Box>
                    )}
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <MemeLoader />
                    </Box>
                ) : numbers.length === 0 ? (
                    <Box sx={{
                        textAlign: 'center', py: 8,
                        borderRadius: 'var(--joy-radius-xl)',
                        border: '1px dashed var(--joy-palette-divider)',
                        bgcolor: 'var(--joy-palette-background-level1)',
                    }}>
                        <PhoneIphoneRoundedIcon sx={{ fontSize: 48, color: 'var(--joy-palette-text-tertiary)', mb: 1 }} />
                        <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)', mb: 2 }}>
                            Пул номеров пуст. Добавь хотя бы один — фотографы смогут получать его для приёма переводов.
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={1.5}>
                        {numbers.map(n => {
                            const meta = BANKS[n.bank] || BANKS.other;
                            return (
                                <Box key={n.id} sx={{
                                    bgcolor: 'var(--joy-palette-background-level1)',
                                    border: '1px solid var(--joy-palette-divider)',
                                    borderRadius: 'var(--joy-radius-lg)',
                                    p: 1.8,
                                    display: 'flex', alignItems: 'center', gap: 1.5,
                                    opacity: n.is_active ? 1 : 0.55,
                                }}>
                                    <BankBadge bank={n.bank} size={42} logoUrl={ n.bank_logo_url ? `${API_URL}${n.bank_logo_url}` : null} color={n.bank_color} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--joy-palette-text-primary)', fontFamily: 'monospace' }}>
                                            {n.phone_formatted || formatPhone(n.phone)}
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-secondary)' }}>
                                            {meta.name} · {n.owner_name}
                                            {n.location_name && (
                                                <span style={{ color: 'var(--joy-palette-text-tertiary)' }}> · {n.location_name}</span>
                                            )}
                                        </Typography>
                                    </Box>
                                    {n.is_currently_claimed && (
                                        <Chip size="sm" sx={{
                                            bgcolor: 'rgba(245,158,11,0.12)', color: '#f59e0b',
                                            fontWeight: 700, fontSize: 10,
                                        }}>
                                            Сейчас выдан
                                        </Chip>
                                    )}
                                    {!n.is_active && (
                                        <Chip size="sm" sx={{
                                            bgcolor: 'rgba(248,113,113,0.12)', color: '#f87171',
                                            fontWeight: 700, fontSize: 10,
                                        }}>
                                            Отключён
                                        </Chip>
                                    )}
                                    {canManage && (
                                        <>
                                            <IconButton size="sm" onClick={() => setEditModal({ open: true, item: n, loading: false, error: '' })}>
                                                <EditRoundedIcon sx={{ fontSize: 18, color: 'var(--joy-palette-primary-300)' }} />
                                            </IconButton>
                                            <IconButton size="sm" disabled={deleting === n.id} onClick={() => handleDelete(n)}>
                                                <DeleteRoundedIcon sx={{ fontSize: 18, color: '#f87171' }} />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            );
                        })}
                    </Stack>
                )}
            </Box>

            <PaymentNumberModal
                open={editModal.open}
                item={editModal.item}
                locations={locations}
                loading={editModal.loading}
                error={editModal.error}
                onClose={() => setEditModal({ open: false, item: null, loading: false, error: '' })}
                onSave={handleSave}
            />

            <BankBrandingModal
                open={brandingOpen}
                onClose={() => { setBrandingOpen(false); loadAll(); }}
                token={token}
                showToast={showToast}
            />
        </motion.div>
    );
}